<template>
  <div :class="['gam-nav-item-link', getClasses, variant]" @click="onClick">
    <div v-if="icon" class="nav-icon">
      <gam-icon v-bind="getIcon" />
    </div>

    <div class="nav-label" :class="{ 'no-arrow': !showArrow }">
      {{ localize(displayName) }}
    </div>

    <div v-if="statusChips?.length" class="status-chips">
      <template v-for="(chip, index) in statusChips" :key="index">
        <gam-chip v-bind="getStatusChip(chip)" />
      </template>
    </div>

    <div v-if="button">
      <gam-button v-bind="getButton" />
    </div>

    <div v-if="getMessageChip" class="nav-messages">
      <gam-chip v-bind="getMessageChip" />
    </div>

    <div v-if="props.messages?.count && props.showMore" class="show-more-info" />

    <span v-if="props.emoji" class="nav-emoji">{{ props.emoji }}</span>

    <div v-if="showArrow" class="nav-arrow">
      <gam-button v-bind="getArrowButton" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import NavigationHelper from '@/core/helpers/navigation.helper';
import { areNotificationsSupported, isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import GamButton from '@/views/components/GamButton.vue';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamNavSize } from '@/views/composables/constants/components/gamNav.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import type { GamIconType } from '@/views/composables/models/components/GamIcon';
import type { GamNavItemLinkType } from '@/views/composables/models/components/GamNav';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps<GamNavItemLinkType & { isActiveNavItemLink?: boolean }>();

const router = useRouter();
const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);
// const sparkStore = useSparkStore();
// const { sparkGambit } = storeToRefs(sparkStore);

const route = useRoute();

const showMessageChip = ref<boolean>(false);

watch(
  () => route,
  () => {
    if (route.path === `/@${userDetails.value?.username}`) {
      showMessageChip.value = false;
    }

    showMessageChip.value = (isMobile.value && (props.isMore || props.isNews)) || !isMobile.value;
  },
  { immediate: true },
);

const isNavItemActive = (): boolean => {
  if (props.isActiveNavItemLink) return true;

  const { link } = props;
  if (!link) return false;

  const currentRoute = router.currentRoute.value.name;
  const isMoreActive = NavigationHelper.isMoreActive(currentRoute) && props.showMore;
  const isCurrentRoute = currentRoute === props.link;

  const isNestedRoute =
    (currentRoute === 'gambit' && link === 'gambits') ||
    (currentRoute === 'club' && link === 'clubs') ||
    (currentRoute === 'player' && link === 'players');

  return props.isActive || isMoreActive || isCurrentRoute || isNestedRoute;
};

const getStatusChip = (chip: GamChipType): GamChipType => {
  return {
    ...chip,
    size: GamChipSize.NORMAL,
  };
};

const onClick = (e: MouseEvent) => {
  props.onClick?.(e);
};

const getClasses = computed(() => ({
  'is-mobile': isMobile.value,
  'icon-only': props.isIconOnly && !isMobile.value,
  'is-more': props.isMore,
  'is-active': isNavItemActive(),
  'show-more': props.showMore,
  'no-arrow': !props.showArrow,
  'is-hidden': isHidden.value,
  small: props.size === GamNavSize.SMALL,
}));

const getMessageChip = computed((): GamChipType | undefined => {
  if (props.messages?.count) {
    return {
      label: props.messages.count.toString(),
      variant: GamChipVariant.PRIMARY,
      size: GamChipSize.X_SMALL,
      isActive: true,
    };
  }
  return undefined;
});

const getIcon = computed((): GamIconType => {
  const iconName = isNavItemActive() ? (props.icon?.split('-empty') as any as GamIconName) : props.icon;

  return {
    name: Array.isArray(iconName) ? (iconName[0] as GamIconName) : props.icon || GamIconName.UNKNOWN,
    size: GamIconSize.X_LARGE,
    isCurrent: true,
  };
});

const getButton = computed((): GamButtonType => {
  return {
    ...props.button,
    variant: GamButtonVariant.SECONDARY,
    size: GamButtonSize.ACTIONS,
  };
});

const getArrowButton = computed((): GamButtonType => {
  return {
    variant: GamButtonVariant.TERTIARY,
    size: GamButtonSize.ACTIONS,
    isIconOnly: true,
    leftIcon: GamIconName.ARROW_RIGHT,
  };
});

const isHidden = computed(() => {
  if (props.name === 'notification' && !areNotificationsSupported.value) {
    return true;
  }

  return false;
});
</script>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;

$message-pop-margin: 6px;

@mixin mobile-nav-emphasis {
  .nav-icon {
    color: var(--color-white-70);
  }

  &.is-more {
    .nav-label {
      color: var(--color-white-100);
    }

    .nav-icon {
      color: var(--color-white-100);
    }
  }
}

@keyframes attention {
  0% {
    filter: none;
  }

  33% {
    filter: none;
  }

  50% {
    filter: saturate(0%) brightness(200%);
  }

  66% {
    filter: none;
  }

  100% {
    filter: none;
  }
}

.gam-nav-item-link {
  width: inherit;
  position: relative;
  -webkit-user-select: none;
  user-select: none;
  min-width: inherit;
  min-height: 68px;
  display: flex;
  align-items: center;
  align-self: stretch;
  border-radius: var(--radius-small);
  border: 1px solid transparent;
  transition: opacity var(--transition);
  gap: 14px;
  cursor: pointer;
  touch-action: manipulation;

  &.show-more {
    transform: translateY(0px);
  }

  &.small {
    min-height: 50px;
  }

  .nav-label {
    width: 100%;
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.05em;
    color: var(--color-white-50);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre;
    flex: 1 0 0;
    transform: translateY(1px);
    --webkit-font-smoothing: antialiased;
  }

  &.small .nav-label {
    font-size: 11px;
  }

  .nav-messages {
    transition: all var(--transition);
    position: absolute;
    // top: var(--spacing-menu);
    right: -6px;
  }

  .nav-emoji {
    font-size: 14px;
    line-height: 1;
  }

  .nav-arrow {
    -webkit-user-select: none;
    user-select: none;
    pointer-events: none;
  }

  .status-chips {
    display: flex;
    gap: var(--spacing-xs);
  }

  &.primary {
    .nav-icon {
      color: var(--color-white-50);
    }

    &.is-active {
      .nav-icon {
        color: var(--color-white);
      }

      .nav-label {
        color: var(--color-white);
      }
    }
  }

  &.hype {
    .nav-icon {
      color: var(--color-pink-70);
    }

    &.is-mobile .nav-icon {
      transform: scale(1.25) translateX(1px);
    }

    &.is-active {
      .nav-icon {
        color: var(--color-pink);
      }

      .nav-label {
        color: var(--color-pink);
        color: var(--color-white);
      }
    }
  }

  &.primary,
  &.hype {
    padding: var(--spacing-l) 26px;

    @include hover {
      background-color: var(--color-dark-500);
    }

    // Nav items only. The `secondary` variant renders settings/profile list
    // rows, which should stay flat on tap.
    @include press;

    &.is-active {
      border-color: var(--color-highlight);
      background: var(--color-fabric-gradient);
    }
  }

  &.hype {
    .nav-icon {
      color: var(--color-pink);
    }
  }

  &.hype.is-sparked :deep(svg) {
    animation: attention 4000ms infinite ease-in-out;
    animation-fill-mode: backwards;
  }

  &.secondary {
    padding: var(--spacing-l) 0;
    border-radius: 0;
    border-bottom: 1px solid var(--color-white-10);

    &.small {
      padding: var(--spacing-md) 0;
    }

    &:last-child {
      border-bottom: none !important;
    }

    @include hover {
      border-bottom: 1px solid var(--color-white-30);
    }

    &.is-active {
      border-bottom: 1px solid var(--color-white-30);
    }
  }

  &.is-mobile {
    padding: 0 var(--spacing-xs);
    gap: var(--spacing-xs);
    min-width: auto;
    transition: border 0s;

    &.primary,
    &.hype {
      border: 0;
    }

    &.primary {
      .nav-icon {
        color: var(--color-white-30);
      }

      &.is-active {
        .nav-icon {
          color: var(--color-white-80);
        }
      }
    }

    &.hype {
      .nav-icon {
        color: var(--color-pink-70);
      }

      &.is-active {
        .nav-icon {
          color: var(--color-pink);
        }
      }
    }

    &.is-active {
      background: none !important;
      border: 0;
    }

    // .nav-label {
    // font-size: 10px;
    // color: var(--color-white-100);
    // font-weight: 400;
    // }

    .show-more-info {
      position: absolute;
      background: var(--color-linear-gradient);
      border-radius: 5px;
      top: 0;
      right: 0;
      width: 5px;
      height: 5px;
    }

    &.is-more {
      width: 52px !important;
      gap: var(--spacing-md);
      flex-direction: row;

      .nav-label {
        font-size: 16px;
        font-weight: 300;
        color: var(--color-white-70);
      }

      .nav-icon {
        color: var(--color-white-70);
      }

      .nav-messages {
        transition: opacity var(--transition);
        position: inherit;
        top: auto;
        right: auto;
      }
    }

    &.is-active {
      @include mobile-nav-emphasis;
    }

    @include hover {
      @include mobile-nav-emphasis;
    }

    .nav-messages {
      right: 42px;
    }
  }

  &.icon-only {
    width: 68px;

    .nav-icon {
      width: auto;
      align-self: center;
    }

    .nav-messages {
      top: $message-pop-margin;
      right: $message-pop-margin;
    }
  }
}

.is-hidden {
  display: none;
}
</style>

<style lang="scss">
.gam-nav.is-collapsed {
  .gam-nav-item-link .nav-messages {
    right: -9px;
  }
}

.gam-nav {
  .gam-nav-item-link .nav-messages {
    right: 26px;
  }
}
</style>
